import { Injectable, Injector, OnDestroy } from '@angular/core';
import { SPINNER_TEXT_TOKEN, SpinnerComponent } from '../../components/spinner/spinner.component';
import { ComponentPortal } from '@angular/cdk/portal';
import { Overlay, OverlayConfig, OverlayRef } from "@angular/cdk/overlay";
import { filter, finalize, timer, Observable, Subscription } from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';

@Injectable()
export class SpinnerService implements OnDestroy{
  
  private spinnerPortal?: ComponentPortal<SpinnerComponent>;
  private overlayRef?: OverlayRef;
  private _isLoading = false;
  private $timerSubscription?: Subscription;

  constructor(private overlay: Overlay, router: Router) {
    router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.done();
    })
  }

  ngOnDestroy(): void {
    this.done()
  }

  async runWithSpinnerAsync<T>(promise: Promise<T>): Promise<T> {
    this.loading();
    let promiseVal: T;
    try {
      promiseVal = await promise;
    } finally {
      this.done()
    }
    return promiseVal;
  }

  onKeyboardClick(event: KeyboardEvent) {
    event.preventDefault()
  }

  onMouseClick(event: MouseEvent) {
    event.preventDefault()
  }

  disableUserInputs(){
    window.addEventListener('click', this.onMouseClick
    );
    window.addEventListener('keydown', this.onKeyboardClick);
  }

  enableUserInputs(){
    window.removeEventListener('click', this.onMouseClick
    );
    window.removeEventListener('keydown', this.onKeyboardClick);
  }

  attachSpinner<T>(observable: Observable<T>): Observable<T> {
    this.loading();
    return observable.pipe(finalize(() => this.done()))
    .pipe(finalize(() => this.dispose()));
  }

  public loading(){
    if (this._isLoading) {
      return;
    }
    this._isLoading = true;
    this.$timerSubscription = timer(1000).subscribe(() => {
      this.disableUserInputs();
      const portalInjector = Injector.create({
        providers: [
          { provide: SPINNER_TEXT_TOKEN, useValue: 'spinner.working'},
        ],
      });

      const config: OverlayConfig = {
        hasBackdrop: true,
        backdropClass: 'spinner-overlay-backdrop',
        panelClass: 'spinner-overlay'
      };
      this.overlayRef = this.overlay.create(config);
      this.spinnerPortal = new ComponentPortal(SpinnerComponent, null, portalInjector);
      this.overlayRef.attach(this.spinnerPortal);
    })
  }

  public done(){
    this.$timerSubscription?.unsubscribe();
    this.enableUserInputs();
    this.overlayRef?.detach();
    this._isLoading = false;
  }

  isLoading() {
    return this._isLoading;
  }

  dispose()
  {
    this.overlayRef?.dispose();
  }
}
